(() => {
  'use strict';

  const rootSelector = '.zm-c13-collection';
  const drawerMedia = window.matchMedia('(max-width: 1023px)');
  let savedOverflow = '';
  let drawerOwnsScrollLock = false;
  const inertSnapshot = new Map();

  const getRoots = () => [...document.querySelectorAll(rootSelector)];

  const setDefaultGrid = (root) => {
    const view = window.matchMedia('(max-width: 767px)').matches ? '2' : '3';
    root.dataset.view = view;
    const grid = root.querySelector('#product-grid');
    if (grid) grid.dataset.view = view;
  };

  const applyVendorFacetRule = (root) => {
    const handle = root.dataset.zmC13Collection;
    if (handle !== 'inspired-perfume-oils' && handle !== 'inspired-perfume-sprays') return;

    root.querySelectorAll(
      '.zm-c13-native-facets input[name="filter.p.vendor"], ' +
      '.zm-c13-native-facets input[name^="filter.p.vendor."]'
    ).forEach((input) => {
      const facet = input.closest('.facets__item') || input.closest('details');
      if (facet) facet.hidden = true;
    });
  };

  const focusableIn = (panel) => [...panel.querySelectorAll(
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
  )].filter((element) => {
    const collapsedDetails = element.closest('details:not([open])');
    return element.getClientRects().length > 0 &&
      (!collapsedDetails || element.matches('summary'));
  });

  const setBackgroundInert = (root, inert) => {
    if (inert) {
      const section = root.closest('.shopify-section') || root;
      const candidates = new Set([...document.body.children]);
      const main = section.closest('#MainContent');
      if (main) [...main.children].forEach((child) => candidates.add(child));
      candidates.forEach((element) => {
        if (element === section || element.contains(section) || section.contains(element)) return;
        if (!inertSnapshot.has(element)) {
          inertSnapshot.set(element, element.hasAttribute('inert'));
          element.setAttribute('inert', '');
        }
      });
      return;
    }
    inertSnapshot.forEach((wasInert, element) => {
      if (wasInert) element.setAttribute('inert', '');
      else element.removeAttribute('inert');
    });
    inertSnapshot.clear();
  };

  const setDrawerOpen = (root, open, returnFocus = true) => {
    const sidebar = root.querySelector('[data-c13-sidebar]');
    const panel = root.querySelector('.zm-c13-filter-panel');
    const trigger = root.querySelector('[data-c13-open]');
    if (!sidebar || !panel) return;

    if (!drawerMedia.matches) {
      const wasOpen = sidebar.dataset.open === 'true';
      sidebar.dataset.open = 'false';
      sidebar.removeAttribute('role');
      sidebar.removeAttribute('aria-modal');
      sidebar.setAttribute('aria-hidden', 'false');
      sidebar.removeAttribute('inert');
      setBackgroundInert(root, false);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (drawerOwnsScrollLock || wasOpen) {
        document.documentElement.style.overflow = savedOverflow;
        drawerOwnsScrollLock = false;
        setBackgroundInert(root, false);
      }
      return;
    }

    if (open) {
      sidebar.dataset.open = 'true';
      sidebar.setAttribute('role', 'dialog');
      sidebar.setAttribute('aria-modal', 'true');
      sidebar.setAttribute('aria-hidden', 'false');
      sidebar.removeAttribute('inert');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (!drawerOwnsScrollLock) {
        savedOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        drawerOwnsScrollLock = true;
      }
      setBackgroundInert(root, true);
      const firstFocus = panel.querySelector('.zm-c13-filter-close') || focusableIn(panel)[0];
      // In Horizon, descendants can retain hidden visibility while the panel
      // itself is already visible. A fixed number of frames is not sufficient.
      const focusStarted = performance.now();
      const focusWhenVisible = () => {
        if (!drawerMedia.matches || sidebar.dataset.open !== 'true' ||
            !firstFocus?.isConnected || panel.contains(document.activeElement)) return;
        if (getComputedStyle(firstFocus).visibility === 'visible' &&
            firstFocus.getClientRects().length > 0) {
          firstFocus.focus({ preventScroll: true });
        } else if (performance.now() - focusStarted < 2000) {
          requestAnimationFrame(focusWhenVisible);
        }
      };
      requestAnimationFrame(focusWhenVisible);
      return;
    }

    const wasOpen = sidebar.dataset.open === 'true';
    sidebar.dataset.open = 'false';
    sidebar.removeAttribute('role');
    sidebar.removeAttribute('aria-modal');
    sidebar.setAttribute('aria-hidden', 'true');
    sidebar.setAttribute('inert', '');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (drawerOwnsScrollLock || wasOpen) {
      document.documentElement.style.overflow = savedOverflow;
      drawerOwnsScrollLock = false;
    }
    setBackgroundInert(root, false);
    if (wasOpen && returnFocus && trigger && drawerMedia.matches) trigger.focus();
  };


  // C17 owner correction: full text in normal flow, no preselection/scroll logic.
  const toggleDescription = (root, toggle) => {
    const remainder = root.querySelector('.zm-c13-description-remainder');
    if (!remainder) return;
    const opening = toggle.getAttribute('aria-expanded') !== 'true';
    remainder.hidden = !opening;
    toggle.setAttribute('aria-expanded', String(opening));
    toggle.textContent = opening ? 'Read less ↑' : 'Read more ↓';
  };

  const initialize = (root) => {
    if (root.dataset.c13Initialized !== 'true') {
      root.dataset.c13Initialized = 'true';
      setDefaultGrid(root);
      setDrawerOpen(root, false, false);
    } else if (drawerMedia.matches) {
      const sidebar = root.querySelector('[data-c13-sidebar]');
      if (sidebar?.dataset.open !== 'true') {
        setDrawerOpen(root, false, false);
      }
    } else {
      setDrawerOpen(root, false, false);
    }
    applyVendorFacetRule(root);
    setDefaultGrid(root);
  };

  document.addEventListener('click', (event) => {
    const root = event.target.closest(rootSelector);
    if (!root) return;

    const descriptionToggle = event.target.closest('.zm-c17-toggle');
    if (descriptionToggle) {
      toggleDescription(root, descriptionToggle);
      return;
    }

    if (event.target.closest('[data-c13-open]')) {
      setDrawerOpen(root, true, false);
      return;
    }

    if (event.target.closest('[data-c13-close]')) {
      setDrawerOpen(root, false);
    }
  });

  document.addEventListener('change', (event) => {
    const priceInput = event.target.closest(`${rootSelector} .zm-c13-facet-form input[name^="filter.v.price."]`);
    if (priceInput && !drawerMedia.matches) {
      if (priceInput.form.checkValidity()) priceInput.form.requestSubmit();
      return;
    }
    const facetCheckbox = event.target.closest(`${rootSelector} .zm-c13-facet-form input[type="checkbox"]`);
    if (facetCheckbox) {
      facetCheckbox.form.requestSubmit();
      return;
    }
    const select = event.target.closest(`${rootSelector} .zm-c13-sort select[name="sort_by"]`);
    if (!select) return;
    const url = new URL(window.location.href);
    url.searchParams.set('filter.v.availability', '1');
    if (select.value) url.searchParams.set('sort_by', select.value);
    else url.searchParams.delete('sort_by');
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  });

  document.addEventListener('keydown', (event) => {
    const root = event.target.closest(rootSelector);
    if (!root) return;
    const sidebar = root.querySelector('[data-c13-sidebar]');
    const panel = root.querySelector('.zm-c13-filter-panel');
    if (!sidebar || sidebar.dataset.open !== 'true' || !panel) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      setDrawerOpen(root, false);
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = focusableIn(panel);
    if (!focusable.length) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  });

  window.addEventListener('resize', () => {
    getRoots().forEach((root) => initialize(root));
  }, { passive: true });

  const initializeAll = () => getRoots().forEach((root) => initialize(root));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }

  const observer = new MutationObserver(() => initializeAll());
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
